import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AwBadgeComponent,
  AwBreadCrumbComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { IntegrationService } from '../../../../../core/services/integration.service';
import { CustomerService } from '../../../../../core/services/customer.service';
import { Customer, Integration } from '../../../../../api/models';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { UpdateOrganizationParams } from '../../../../../types/types';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '../../../../../core/services/theme.service';
import {
  Organization,
  UserService,
} from '../../../../../core/services/user.service';
import {ClientSecretService} from '../../../../../core/services/client-secret.service';
import { ApiAccessSectionComponent } from '../../../../../shared/components/api-access-section/api-access-section.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { SingleSelectOption } from '@assetworks-llc/aw-component-lib';

/**
 * Pure function: builds dropdown options from table data and associated customer IDs.
 * Exported for property-based testing.
 */
export function buildAssociatedCustomerOptions(
  tableData: any[],
  customerIds: string[] | undefined
): SingleSelectOption[] {
  const idSet = new Set(customerIds ?? []);
  return tableData
    .filter((row: any) => idSet.has(row.customerInfo.customerId))
    .map((row: any) => ({
      label: row.customerInfo.customerName,
      value: row.customerInfo.customerId,
    }));
}

@Component({
  selector: 'app-customer-details',
  standalone: true,
  imports: [
    CommonModule,
    AwBreadCrumbComponent,
    AwDividerComponent,
    AwSearchComponent,
    AwTableComponent,
    AwSpinnerComponent,
    AwDialogComponent,
    AwToastComponent,
    AwButtonDirective,
    MatIcon,
    FormsModule,
    ApiAccessSectionComponent,
    AwDropdownFormComponent,
  ],
  templateUrl: './integration-details.component.html',
  styleUrls: [
    './integration-details.component.scss',
    '../../../../../../styles/management.scss',
  ],
})
export class IntegrationDetailsComponent implements OnInit {
  AuthTypeMap = {
    0: 'OAuth2',
    1: 'EntraID',
    2: 'OpenID Connect',
    3: 'N/A',
  } as const;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private integrationService: IntegrationService,
    private themeService: ThemeService,
    private userService: UserService,
    private clientSecretService: ClientSecretService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  openDeleteCustomerDialogSingle: boolean = false;
  openChangeCustomerStatus: boolean = false;
  openAssociateCustomerDialog: boolean = false;

  container_theme = computed(() => this.themeService.themeSignal());

  customersInfo: any[] = [];
  singleCustomerToDelete: TableRow | null = null;
  multipleCustomersToDelete: TableRow[] | null = null;

  userPermissions: Record<string, boolean> = {};
  organization: Organization | null = null;

  integrationInfo!: Integration | undefined;
  integrationInfoDividers = signal<any[]>([]);
  integrationDateDividers = signal<any[]>([]);

  customersTableData = signal<any[]>([]);
  customersTableDataCopy: any[] = [];
  disableCustomerChangeStatus: boolean = true;
  disableCustomerAssociate: boolean = true;
  customerSearchValue!: string;
  checkedRows: TableRow[] = [];

  generatedClientSecret: string = '';
  generatedClientId: string = '';

  selectedCustomerId: string = '';
  associatedCustomerOptions = signal<SingleSelectOption[]>([]);
  hasAssociatedCustomers = computed<boolean>(() => this.associatedCustomerOptions().length > 0);

  dialogOptionsDeleteSingle: DialogOptions = {
    variant: 'alert',
    title: `Change Customer Association`,
    description:
      'By selecting Continue, you will disassociate the selected customer from this Integration Partner. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  dialogOptionsChangeStatus: DialogOptions = {
    variant: 'standard',
    title: `Change Customer Status`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  };

  dialogOptionsAssociate: DialogOptions = {
    variant: 'alert',
    title: 'Associate Customers',
    description: 'By selecting Continue, you will associate the selected customer(s) with this Integration Partner. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  changeStatusFormFields = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  headerButtonActions = signal<any[]>([
    {
      iconButtonCallback: {
        icon: 'edit',
        action: () => {
          this.router.navigate(['edit'], { relativeTo: this.route });
        },
      },
    },
  ]);

  customersColumnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'customerInfo',
      label: 'Customer Name/ID',
      combineFields: [
        'customerInfo.customerName',
        'customerInfo.detailsPageRoute',
        'customerInfo.customerId',
      ],
      combineTemplate: (values: any[]) => ({
        template: this.CustomTemplate(values),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Associated',
      label: 'Associated',
      combineFields: ['associationBadge.color', 'associationBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
  ];
  CustomTemplate = (values: any) => `
  <div class="custom-template">
    <a href="${values[1]}">${values[0]}</a>
  <p>${values[2]}</p>
  </div>`;

  breadcrumbs = signal<BreadCrumb[]>([]);

  private rebuildAssociatedCustomerOptions(): void {
    this.associatedCustomerOptions.set(
      buildAssociatedCustomerOptions(this.customersTableData(), this.integrationInfo?.customerIds)
    );
  }

  onCustomerSelected(option: SingleSelectOption | null): void {
    if (!option) {
      this.selectedCustomerId = '';
      return;
    }
    const newId = option.value as string;
    if (newId === this.selectedCustomerId) {
      return;
    }
    this.selectedCustomerId = '';
    setTimeout(() => {
      this.selectedCustomerId = newId;
    });
  }

  buildAllData(): void {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();

    this.route.paramMap
      .pipe(
        map((params) => {
          this.breadcrumbs.set([
            ...this.breadcrumbs(),
            {
              label: 'Integration Details',
              link: `/integration-management/integration-details/${params.get(
                'integrationId'
              )!}`,
              newTab: false,
            },
          ]);
          return params.get('integrationId')!;
        }),
        take(1),
        switchMap((integrationId) =>
          this.integrationService.getIntegrationById({
            organizationId: integrationId,
          })
        ),
        switchMap((integrationInfo) => {
          if (!integrationInfo) {
            return of([]);
          }

          this.integrationInfo = integrationInfo;

          this.integrationDateDividers.set([
            {
              title: 'Created Date',
              info: this.integrationInfo?.createdOn
                ? new Date(this.integrationInfo?.createdOn).toLocaleDateString()
                : 'N/A',
            },
            {
              title: 'Last Modified Date',
              info: this.integrationInfo?.modifiedOn
                ? new Date(
                    this.integrationInfo?.modifiedOn
                  ).toLocaleDateString()
                : 'N/A',
            },
          ]);

          // For integration employees, fetch only associated customers
          // For AssetWorks employees, fetch all customers
          if (this.organization === 'integration-employee') {
            const customerIds = integrationInfo.customerIds || [];
            if (customerIds.length === 0) {
              return of([]);
            }
            return forkJoin(
              customerIds.map(id =>
                this.customerService.getCustomerById({ organizationId: id })
                  .pipe(catchError(() => of(null)))
              )
            ).pipe(
              map(customers => customers.filter((c): c is Customer => c !== null))
            );
          } else {
            return this.customerService.getAllCustomers().pipe(
              catchError((error) => {
                this.buildToastMessage('Failed to load customers', 'error');
                return of([]);
              }),
              map((customers) => customers || [])
            );
          }
        }),
        switchMap((customers) => {
          if (this.integrationInfo?.organizationId && this.userPermissions['isAdministrator']) {
            return this.clientSecretService
              .getClientSecrets({
                organizationId: this.integrationInfo.organizationId
              })
              .pipe(
                map((secrets) => ({ customers, secrets })),
                catchError(() => of({ customers, secrets: undefined }))
              );
          }
          return of({ customers, secrets: undefined });
        }),
        catchError((error) => {
          this.buildToastMessage('Failed to load integration data', 'error');
          return of({ customers: [], secrets: undefined });
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(({ customers, secrets }) => {
        // Build Info Display
        const infoDividers = [
          {
            title: 'Integration Partner Name',
            info:
              this.integrationInfo?.organizationName ?? 'Unable to Retrieve',
          },
          {
            title: 'Integration ID',
            info:
              this.integrationInfo?.organizationId ?? 'Unable to Retrieve',
          },
          {
            title: 'Status',
            info: this.integrationInfo?.enabled ? 'Active' : 'Disabled',
          },
        ];

        if (this.organization === 'assetworks-employee' && this.userPermissions['isAdministrator']) {
          infoDividers.push({
            title: 'Client Secret Expiration Date',
            info: secrets && Object.keys(secrets).length > 0
              ? (() => {
                  const dates = Object.values(secrets).map(date => new Date(date)).filter(date => !isNaN(date.getTime()));
                  return dates.length > 0
                    ? new Date(Math.max(...dates.map(date => date.getTime()))).toLocaleDateString()
                    : 'No valid expiration dates';
                })()
              : 'No client secret expiration dates found',
          });
        }

        this.integrationInfoDividers.set(infoDividers);

        // Build Table
        const integrationCustomersData: any[] =
          customers?.map((customer) => {
            const isAssociated = this.integrationInfo?.customerIds?.includes(customer?.organizationId ?? '') ?? false;
            return {
              customerInfo: {
                customerName: customer?.organizationName ?? '',
                customerId: customer?.organizationId ?? '',
                customerIntegrationPartnerIDs:
                  customer?.integrationPartnerIds ?? [],
                detailsPageRoute: `/customer-management/customer-details/${customer?.organizationId}`,
              },
              associationBadge: {
                isAssociated: isAssociated,
                color: isAssociated ? 'success-green-700' : 'error-red',
                label: isAssociated ? 'Yes' : 'No',
              },
              badgeNested: {
                enabled: customer?.enabled ?? false,
                color: customer?.enabled
                  ? 'success-green-700'
                  : 'aw-dark-gray-1000',
                label: customer?.enabled ? 'Active' : 'Disabled',
              },
              authType: customer?.authenticationType,
            };
          }) ?? [];
        this.customersTableData.set(integrationCustomersData);
        this.customersTableDataCopy = integrationCustomersData;
        this.rebuildAssociatedCustomerOptions();
      });
  }

  ngOnInit(): void {
    this.userPermissions = this.userService.getUserPermissions();
    this.organization = this.userService.getOrganization();

    if (this.organization === 'assetworks-employee') {
      this.breadcrumbs.set([
        ...this.breadcrumbs(),
        {
          label: 'Integration Partner Management',
          link: '/integration-management/overview',
          newTab: false,
        },
      ]);
    }

    if (this.organization === 'integration-employee') {
      const associatedColumnIndex = this.customersColumnsDefinition.findIndex(
        col => col.key === 'Associated'
      );
      if (associatedColumnIndex !== -1) {
        this.customersColumnsDefinition.splice(associatedColumnIndex, 1);
      }
    }

    if (this.organization === 'assetworks-employee' && (this.userPermissions['canDelete'] || this.userPermissions['canEdit'])) {
      this.customersColumnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.organization === 'assetworks-employee' && this.userPermissions['canDelete']) {
      this.customersColumnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'deleteButton',
        label: 'Disassociate',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',

        action: (data: TableRow) => {
          if (data['associationBadge']?.isAssociated) {
            this.openDeleteCustomerDialogSingle = true;
            this.singleCustomerToDelete = data;
          }
        },
      });
    }

    this.buildAllData();
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    const associatedRows = this.checkedRows.filter(row => row['associationBadge']?.isAssociated);
    const nonAssociatedRows = this.checkedRows.filter(row => !row['associationBadge']?.isAssociated);

    this.disableCustomerChangeStatus = associatedRows.length === 0;
    this.disableCustomerAssociate = nonAssociatedRows.length === 0;
    this.multipleCustomersToDelete = associatedRows;
  }
  onSearchTable(searchInput: string) {
    this.awTableComponent.applyFilter(searchInput);
  }
  dialogFormCloseHandler(key: string) {
    if (key === 'delete') {
      this.openDeleteCustomerDialogSingle = false;
    } else if (key === 'change-status') {
      this.openChangeCustomerStatus = false;
    } else if (key === 'associate') {
      this.openAssociateCustomerDialog = false;
    }
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  deleteCustomerFormSubmit(): void {
    if (this.singleCustomerToDelete === null) {
      this.buildToastMessage('No customer selected for deletion', 'error');
      return;
    }

    this.isLoading = true;

    const removedCustomerId: string =
      this.singleCustomerToDelete['customerInfo'].customerId;
    const newCustomerIds: string[] = (
      this.integrationInfo?.customerIds ?? []
    ).filter((customerId) => customerId !== removedCustomerId);

    const integrationParams: UpdateOrganizationParams = {
      organizationAuthType: this.integrationInfo?.authenticationType,
      organizationName: this.integrationInfo?.organizationName ?? '',
      organizationStatus: this.integrationInfo?.enabled || false,
      organizationPartnerIds: newCustomerIds,
      organizationId: this.integrationInfo?.organizationId || '',
    };
    this.integrationService
      .updateIntegration(integrationParams)
      .subscribe((removed) => {
        if (removed) {
          this.buildToastMessage('Successfully disassociated customer', 'success');
          
          // Update integration info
          this.integrationInfo!.customerIds = newCustomerIds;
          
          // Update table data to show disassociation
          const customerToUpdate = this.customersTableDataCopy.find(
            (customer) => customer['customerInfo'].customerId === removedCustomerId
          );
          if (customerToUpdate) {
            customerToUpdate['associationBadge'].isAssociated = false;
            customerToUpdate['associationBadge'].color = 'error-red';
            customerToUpdate['associationBadge'].label = 'No';
          }
          this.customersTableData.set([...this.customersTableDataCopy]);
          this.rebuildAssociatedCustomerOptions();
          if (removedCustomerId === this.selectedCustomerId) {
            this.selectedCustomerId = '';
          }
        } else {
          this.buildToastMessage('Failed to disassociate customer', 'error');
        }
        this.openDeleteCustomerDialogSingle = false;
        this.isLoading = false;
      });
  }

  changeCustomerStatusFormSubmit(formValues: any): void {
    if (!formValues.valid) {
      this.buildToastMessage('Please select a status', 'alert');
      return;
    }
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }
    const status: boolean = formValues.formData.changeStatus.value;

    const associatedCustomers = this.checkedRows.filter(r => r['associationBadge']?.isAssociated);
    const customersToChange = associatedCustomers.filter(
      (r) => r['badgeNested'].enabled !== status
    );
    if (!customersToChange.length) {
      this.buildToastMessage(
        `No ${!status ? 'active' : 'disabled'} rows selected`,
        'alert'
      );
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = customersToChange.map(
      (customer) => {
        const customerInfo = customer['customerInfo'];
        const statusInfo = customer['badgeNested'];
        const customerParams: UpdateOrganizationParams = {
          organizationAuthType: customerInfo.customerAuthType,
          organizationName: customerInfo.customerName,
          organizationStatus: !statusInfo.enabled,
          organizationPartnerIds: customerInfo.customerIntegrationPartnerIDs,
          organizationId: customerInfo.customerId,
        };

        return this.customerService
          .updateCustomer(customerParams)
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeCustomerStatus = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates: number = results.filter(
          (updated) => updated
        ).length;

        customersToChange.forEach((customer, index) => {
          if (results[index]) {
            const badge = customer['badgeNested'];

            badge.enabled = status;
            badge.color = badge.enabled
              ? 'success-green-700'
              : 'aw-dark-gray-1000';
            badge.label = badge.enabled ? 'Active' : 'Disabled';
          }
        });

        if (numStatusUpdates === customersToChange.length) {
          this.buildToastMessage(
            `Successfully ${
              status ? 'enabled' : 'disabled'
            } all selected customers`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `ALERT: ${numStatusUpdates} of ${
              customersToChange.length
            } customers ${status ? 'enabled' : 'disabled'}`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            `Failed to ${status ? 'enable' : 'disable'} any selected customers`,
            'error'
          );
        }
      });
  }

  associateCustomersFormSubmit(): void {
    const nonAssociatedCustomers = this.checkedRows.filter(r => !r['associationBadge']?.isAssociated);

    if (!nonAssociatedCustomers.length) {
      this.buildToastMessage('No non-associated customers selected', 'error');
      return;
    }

    this.isLoading = true;

    const customerIdsToAdd = nonAssociatedCustomers.map(c => c['customerInfo'].customerId);
    const newCustomerIds = [...(this.integrationInfo?.customerIds ?? []), ...customerIdsToAdd];

    const integrationParams: UpdateOrganizationParams = {
      organizationAuthType: this.integrationInfo?.authenticationType,
      organizationName: this.integrationInfo?.organizationName ?? '',
      organizationStatus: this.integrationInfo?.enabled || false,
      organizationPartnerIds: newCustomerIds,
      organizationId: this.integrationInfo?.organizationId || '',
    };

    this.integrationService.updateIntegration(integrationParams)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.openAssociateCustomerDialog = false;
      }))
      .subscribe((success) => {
        if (success) {
          this.integrationInfo!.customerIds = newCustomerIds;

          nonAssociatedCustomers.forEach(customer => {
            customer['associationBadge'].isAssociated = true;
            customer['associationBadge'].color = 'success-green-700';
            customer['associationBadge'].label = 'Yes';
          });

          this.rebuildAssociatedCustomerOptions();
          this.buildToastMessage(`Successfully associated ${customerIdsToAdd.length} customer(s)`, 'success');
        } else {
          this.buildToastMessage('Failed to associate customers', 'error');
        }
      });
  }
}
